"use client";

import { ArrowRight, Mail, MessageSquare, Webhook } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Specifications() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: tilesRef,
    isVisible: tilesVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const emailSpecs = [{
    label: "SERVICE",
    value: "Field Diagnostics"
  }, {
    label: "RESPONSE",
    value: "24/7 Support"
  }, {
    label: "PARTS",
    value: "Genuine Siemens"
  }, {
    label: "WARRANTY",
    value: "Certified"
  }];
  const smsSpecs = [{
    label: "COVERAGE",
    value: "Global Logistics"
  }, {
    label: "SPEED",
    value: "Express Shipping",
    accent: true
  }, {
    label: "TRACKING",
    value: "Real-time updates"
  }, {
    label: "SUPPORT",
    value: "Dedicated team"
  }];
  const webhookSpecs = [{
    label: "PROTOCOL",
    value: "Secure API"
  }, {
    label: "INTEGRATION",
    value: "Seamless sync"
  }, {
    label: "SECURITY",
    value: "Encrypted"
  }, {
    label: "UPTIME",
    value: "99.99%"
  }];
  return <section data-section-id="15630" id="platform-specifications" className="bg-background text-foreground border-t border-b border-border py-24 md:py-32">
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-16">
          {}
          <div ref={headRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-[0.6875rem] font-medium tracking-[0.12em] uppercase text-muted-foreground mb-8">
                {"// SERVICE SPECIFICATIONS"}
              </div>
              <h2 className="font-default font-medium text-foreground leading-[1.04] mb-8" style={{
              fontSize: "clamp(2rem, 3.8vw, 3.25rem)",
              letterSpacing: "-0.028em"
            }}>
                Expert solutions.
                <br />
                Built for <em className="italic font-normal">reliability</em>.
              </h2>
              <p className="font-default text-muted-foreground mb-10" style={{
              fontSize: "clamp(1.0625rem, 1.25vw, 1.1875rem)",
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>
                Our technical services ensure your industrial equipment operates at peak performance. From field diagnostics to genuine parts procurement, we provide the support your infrastructure demands.
              </p>
              <Link to="/SolutionsPage"><Button variant="outline" className="h-11 px-6 bg-transparent border border-border hover:border-primary hover:bg-accent text-foreground rounded-md font-medium text-[0.9375rem] group">
                  View our solutions
                  <ArrowRight className="size-4 ml-1 transition-transform duration-200 group-hover:translate-x-1" />
                </Button></Link>
            </div>
          </div>

          {}
          <div ref={tilesRef} className="lg:col-span-7">
            <div className="flex flex-col">
              {}
              <ChannelTile data-index={0} icon={<Mail className="size-5" strokeWidth={1.5} />} eyebrow="SERVICE · FIELD" title="On-site technical support" description="Professional field diagnostics and maintenance services performed by certified technicians." specs={emailSpecs} isVisible={tilesVisible} delay={0} isFirst />

              {}
              <ChannelTile data-index={1} icon={<MessageSquare className="size-5" strokeWidth={1.5} />} eyebrow="SERVICE · LOGISTICS" title="Genuine parts supply" description="Fast, reliable shipping of authentic Siemens components to keep your operations running smoothly." specs={smsSpecs} isVisible={tilesVisible} delay={80} />

              {}
              <ChannelTile data-index={2} icon={<Webhook className="size-5" strokeWidth={1.5} />} eyebrow="SERVICE · DIGITAL" title="System integration" description="Secure and efficient digital tools to monitor your equipment health and manage maintenance schedules." specs={webhookSpecs} isVisible={tilesVisible} delay={160} code />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ChannelTile({
  icon,
  eyebrow,
  title,
  description,
  specs,
  isVisible,
  delay,
  isFirst,
  code,
  ...rest
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  specs: {
    label: string;
    value: string;
    accent?: boolean;
  }[];
  isVisible: boolean;
  delay: number;
  isFirst?: boolean;
  code?: boolean;
  [key: string]: unknown;
}) {
  return <div {...rest} className={`group border-b border-border ${isFirst ? "border-t" : ""} py-8 px-1 transition-all duration-500 ease-out hover:bg-accent/50`} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(8px)",
    transitionDelay: `${delay}ms`
  }}>
      <div className="grid grid-cols-12 gap-4 md:gap-6 items-start px-5 md:px-6">
        {}
        <div className="col-span-12 md:col-span-1 text-muted-foreground group-hover:text-primary transition-colors duration-200">
          {icon}
        </div>

        {}
        <div className="col-span-12 md:col-span-11 -mt-1">
          <div className="font-mono text-[0.6875rem] font-medium tracking-[0.12em] uppercase text-muted-foreground mb-3">
            {eyebrow}
          </div>
          <h3 className="font-default text-foreground font-medium mb-2" style={{
          fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.2
        }}>
            {title}
          </h3>
          <p className="font-default text-muted-foreground text-[0.9375rem] leading-[1.6] mb-6 max-w-[52ch]">
            {description}
          </p>

          {}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-5">
            {specs.map((spec, i) => <div key={i} data-index={i} className="flex items-baseline justify-between gap-3 py-1 font-mono text-[0.75rem] tabular-nums">
                <span className="text-muted-foreground tracking-[0.04em] uppercase whitespace-nowrap">
                  {spec.label}
                </span>
                <span className="text-border select-none flex-1 mx-2 border-b border-dotted border-border translate-y-[-3px]" />
                <span className={`text-right ${spec.accent ? "text-primary font-medium" : "text-foreground"}`}>
                  {spec.value}
                </span>
              </div>)}
          </div>

          {}
          {code && <div className="mt-6 bg-muted border border-border rounded-md p-5 overflow-x-auto">
              <pre className="font-mono text-[0.75rem] leading-[1.6] text-foreground tabular-nums">
{`GET /api/v1/diagnostics/status
Authorization: Bearer <token>

{
  "system_id": "siemens_unit_01",
  "status": "operational",
  "last_maintenance": "2025-01-14",
  "next_service": "2025-07-14",
  "performance_index": 98.4,
  "alerts": []
}`}
              </pre>
            </div>}
        </div>
      </div>
    </div>;
}